import React, { useState, useCallback } from "react";
import "../css/Common.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faPalette,
  faGlobe,
  faChevronRight,
  faLock,
  faCheckCircle,
  faExclamationTriangle,
  faShieldAlt,
  faUserShield,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";

// Import context
import { useTheme } from "../context/ThemeContext";
import { useUser } from "../context/UserContext";
import { useEdition } from "../context/EditionContext";
import { useTranslation } from "../../../i18n/LanguageContext";

// Import sub-components
import SecuritySettings from "./components/SecuritySettings";
import NotificationSettings from "./components/NotificationSettings";
import AppearanceSettings from "./components/AppearanceSettings";
import LocalizationSettings from "./components/LocalizationSettings";
import PrivacySettings from "./components/PrivacySettings";
import OrganizationSettings from "./components/OrganizationSettings";

// Import custom hooks
import { useRoleCheck } from "../../../hooks/useRoleCheck";
import { useThemeHelpers } from "./hooks/useThemeHelpers";

// Import shared accordion CSS
import "../css/Accordion.css";

function Settings({ initialSection, initialAction }) {
  const { theme, setTheme, effectiveTheme } = useTheme();
  const { canAccessAccountManagement, isAccountAdmin } = useRoleCheck();
  const { isDemoSeat } = useUser();
  const { isCorporate } = useEdition();
  const { t, language } = useTranslation();

  // Organization-wide controls are only relevant to owners of a corporate
  // (business) account. EditionContext.isCorporate is the available signal at
  // this layer; the OrganizationSettings component re-confirms via the account API.
  const showOrganizationSection = isCorporate && isAccountAdmin();

  // Exclusive accordion — auto-expand if initialSection provided, else all collapsed
  const [expandedSection, setExpandedSection] = useState(
    initialSection || null,
  );

  // Auto-expand and scroll to section when navigated with initialSection
  React.useEffect(() => {
    if (initialSection) {
      setExpandedSection(initialSection);
      setTimeout(() => {
        const el = document.getElementById(
          `settings-section-${initialSection}`,
        );
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    }
  }, [initialSection]);

  const toggleSection = (sectionId) => {
    setExpandedSection((prev) => {
      const isOpening = prev !== sectionId;
      if (isOpening) {
        setTimeout(() => {
          const el = document.getElementById(`settings-section-${sectionId}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 50);
      }
      return isOpening ? sectionId : null;
    });
  };

  // MFA status for accordion badge
  const [mfaPosture, setMfaPosture] = useState({
    enabled: false,
    method: null,
  });

  // Use theme helper functions
  const { handleThemeChange, getThemeIcon, getThemeLabel } = useThemeHelpers(
    theme,
    setTheme,
  );

  // Callback for SecuritySettings to report MFA status
  const handleMfaStatusChange = useCallback((status) => {
    setMfaPosture({
      enabled: status.enabled || false,
      method: status.method || null,
    });
  }, []);

  // Accordion section renderer
  const renderAccordion = ({
    id,
    icon,
    iconClass,
    title,
    subtitle,
    statusBadge,
    expanded,
    onToggle,
    children,
    demoDisabled,
  }) => (
    <div
      className="settings-accordion"
      id={`settings-section-${id}`}
      title={demoDisabled ? "Not Available in Demo Mode" : undefined}
      style={
        demoDisabled ? { opacity: 0.45, cursor: "not-allowed" } : undefined
      }
    >
      <div
        className="settings-accordion-header"
        onClick={demoDisabled ? undefined : onToggle}
        style={demoDisabled ? { pointerEvents: "none" } : undefined}
      >
        <div className={`accordion-icon ${iconClass}`}>
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="accordion-title-group">
          <h3 className="accordion-title">{title}</h3>
          <p className="accordion-subtitle">
            {demoDisabled ? (
              <span
                style={{
                  color: "var(--text-secondary, #888)",
                  fontStyle: "italic",
                }}
              >
                {t("common.notAvailableDemo")}
              </span>
            ) : (
              subtitle
            )}
          </p>
        </div>
        {statusBadge && !demoDisabled && (
          <span
            className={`accordion-status-badge ${statusBadge.className || ""}`}
          >
            {statusBadge.icon && <FontAwesomeIcon icon={statusBadge.icon} />}
            {statusBadge.label}
          </span>
        )}
        {demoDisabled && (
          <span className="accordion-status-badge badge-warning">
            {t("common.demoMode")}
          </span>
        )}
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`accordion-chevron ${expanded ? "expanded" : ""}`}
        />
      </div>
      <div
        className={`settings-accordion-content ${expanded ? "expanded" : ""}`}
      >
        {expanded && children}
      </div>
    </div>
  );

  return (
    <div className="common-container">
      <div className="common-header">
        <h1 className="main-title">{t("settings.title")}</h1>
        <p className="common-subtitle">{t("settings.subtitle")}</p>
      </div>

      {/* ── Stat-Cards Strip ── */}
      <div
        className="page-stats-strip"
        style={{ maxWidth: 900, margin: "0 auto 24px", padding: "0 8px" }}
      >
        <div className="page-stat-card">
          <div className="page-stat-icon security">
            <FontAwesomeIcon icon={faShieldAlt} />
          </div>
          <div className="page-stat-info">
            <span className="page-stat-label">
              {t("settings.stats.mfaStatus")}
            </span>
            <span className="page-stat-value">
              <span
                className={`page-status-badge ${mfaPosture.enabled ? "on" : "off"}`}
              >
                {mfaPosture.enabled
                  ? t("common.enabled")
                  : t("common.disabled")}
              </span>
            </span>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon theme">
            <FontAwesomeIcon icon={faPalette} />
          </div>
          <div className="page-stat-info">
            <span className="page-stat-label">{t("settings.stats.theme")}</span>
            <span className="page-stat-value">{getThemeLabel(theme)}</span>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon notify">
            <FontAwesomeIcon icon={faBell} />
          </div>
          <div className="page-stat-info">
            <span className="page-stat-label">
              {t("settings.stats.notifications")}
            </span>
            <span className="page-stat-value">{t("common.configured")}</span>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon locale">
            <FontAwesomeIcon icon={faGlobe} />
          </div>
          <div className="page-stat-info">
            <span className="page-stat-label">
              {t("settings.stats.language")}
            </span>
            <span className="page-stat-value">
              {t(
                language && language.toLowerCase().startsWith("es")
                  ? "localization.lang.es"
                  : "localization.lang.enUS",
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="settings-single-page">
        {/* Account Security Accordion */}
        {renderAccordion({
          id: "security",
          icon: faLock,
          iconClass: "security",
          title: t("settings.section.security.title"),
          subtitle: t("settings.section.security.subtitle"),
          demoDisabled: isDemoSeat,
          expanded: expandedSection === "security",
          onToggle: () => toggleSection("security"),
          children: (
            <SecuritySettings
              onMfaStatusChange={handleMfaStatusChange}
              isDemoSeat={isDemoSeat}
              initialAction={
                initialSection === "security" ? initialAction : undefined
              }
            />
          ),
        })}

        {/* Privacy & Sharing Accordion */}
        {renderAccordion({
          id: "privacy",
          icon: faUserShield,
          iconClass: "security",
          title: t("settings.section.privacy.title"),
          subtitle: t("settings.section.privacy.subtitle"),
          statusBadge: null,
          expanded: expandedSection === "privacy",
          onToggle: () => toggleSection("privacy"),
          children: <PrivacySettings />,
        })}

        {/* Organization Accordion — corporate account owners only */}
        {showOrganizationSection &&
          renderAccordion({
            id: "organization",
            icon: faBuilding,
            iconClass: "security",
            title: t("settings.section.organization.title"),
            subtitle: t("settings.section.organization.subtitle"),
            statusBadge: null,
            expanded: expandedSection === "organization",
            onToggle: () => toggleSection("organization"),
            children: <OrganizationSettings />,
          })}

        {/* Appearance & Display Accordion */}
        {renderAccordion({
          id: "appearance",
          icon: faPalette,
          iconClass: "appearance",
          title: t("settings.section.appearance.title"),
          subtitle: t("settings.section.appearance.subtitle"),
          statusBadge: null,
          expanded: expandedSection === "appearance",
          onToggle: () => toggleSection("appearance"),
          children: (
            <AppearanceSettings
              theme={theme}
              setTheme={setTheme}
              effectiveTheme={effectiveTheme}
              handleThemeChange={handleThemeChange}
              getThemeIcon={getThemeIcon}
              getThemeLabel={getThemeLabel}
            />
          ),
        })}

        {/* Language & Region Accordion */}
        {renderAccordion({
          id: "localization",
          icon: faGlobe,
          iconClass: "localization",
          title: t("settings.section.localization.title"),
          subtitle: t("settings.section.localization.subtitle"),
          statusBadge: null,
          expanded: expandedSection === "localization",
          onToggle: () => toggleSection("localization"),
          children: <LocalizationSettings />,
        })}

        {/* Notifications & Announcements Accordion */}
        {renderAccordion({
          id: "notifications",
          icon: faBell,
          iconClass: "notifications",
          title: t("settings.section.notifications.title"),
          subtitle: t("settings.section.notifications.subtitle"),
          statusBadge: null,
          expanded: expandedSection === "notifications",
          onToggle: () => toggleSection("notifications"),
          children: <NotificationSettings />,
        })}
      </div>
    </div>
  );
}

export default Settings;
